import { useRef, useCallback, useEffect } from 'react';
import { Bold, Italic, List, ListOrdered, Undo, Redo } from 'lucide-react';

export default function SimpleRichEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);

  const execCmd = useCallback((command, val = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    if (onChange) onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const handleInput = useCallback(() => {
    if (onChange) onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  useEffect(() => {
    if (!value && editorRef.current && editorRef.current.innerHTML !== '') {
      editorRef.current.innerHTML = '';
    }
  }, [value]);

  const buttons = [
    { cmd: 'bold', icon: <Bold size={16} />, label: 'Bold' },
    { cmd: 'italic', icon: <Italic size={16} />, label: 'Italic' },
    { cmd: 'insertUnorderedList', icon: <List size={16} />, label: 'Bullet List' },
    { cmd: 'insertOrderedList', icon: <ListOrdered size={16} />, label: 'Numbered List' },
    { cmd: 'undo', icon: <Undo size={16} />, label: 'Undo' },
    { cmd: 'redo', icon: <Redo size={16} />, label: 'Redo' },
  ];

  return (
    <div className="border border-gray-300 rounded-md overflow-hidden">
      <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-300">
        {buttons.map((btn) => (
          <button
            key={btn.cmd}
            type="button"
            title={btn.label}
            onMouseDown={(e) => { e.preventDefault(); execCmd(btn.cmd); }}
            className="p-1.5 rounded hover:bg-gray-200 text-gray-700 transition-colors"
          >
            {btn.icon}
          </button>
        ))}
      </div>
      <style>{`
        [data-testid="email-rich-editor"] ul { list-style-type: disc; padding-left: 1.5em; margin: 0.5em 0; }
        [data-testid="email-rich-editor"] ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.5em 0; }
        [data-testid="email-rich-editor"] li { margin: 0.25em 0; }
      `}</style>
      <div
        ref={editorRef}
        contentEditable
        data-testid="email-rich-editor"
        onInput={handleInput}
        onPaste={handlePaste}
        className="min-h-[200px] px-3 py-2 font-manrope text-base focus:outline-none"
        style={{ whiteSpace: 'pre-wrap' }}
        data-placeholder={placeholder || 'Type your message...'}
        suppressContentEditableWarning
      />
    </div>
  );
}
